import { Breadcrumb } from '@/components/common/Breadcrumb';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Mic, 
  Send, 
  Loader2, 
  CheckCircle,
  Award,
  Users,
  DollarSign,
  Globe,
  Star,
  ArrowRight
} from 'lucide-react';
import { contactAPI } from '@/services/api';
import { toast } from 'sonner';

export default function JoinSpeakerPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    linkedin: '',
    expertise: '',
    experience: '',
    topics: '',
    bio: '',
    agreeTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agreeTerms) {
      toast.error('Please agree to the terms to continue');
      return;
    }
    
    setLoading(true);

    try {
      await contactAPI.submit({
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        subject: 'Speaker Panel Application',
        message: `
Company: ${formData.company}
Title: ${formData.title}
LinkedIn: ${formData.linkedin}
Expertise: ${formData.expertise}
Experience: ${formData.experience}
Topics: ${formData.topics}
Bio: ${formData.bio}
        `.trim(),
      });
      setSuccess(true);
      toast.success('Application submitted successfully!');
    } catch (err) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Globe, title: 'Global Reach', description: 'Present to thousands of compliance professionals worldwide' },
    { icon: DollarSign, title: 'Competitive Pay', description: 'Earn competitive honorariums for each session' },
    { icon: Award, title: 'Build Authority', description: 'Establish yourself as an industry thought leader' },
    { icon: Users, title: 'Network', description: 'Connect with peers and industry leaders' },
  ];

  const expertiseAreas = [
    'Risk Management',
    'Quality Management',
    'Corporate Governance',
    'IT & Cybersecurity',
    'HR Compliance',
    'Healthcare Compliance',
    'Financial Compliance',
    'OSHA & Safety',
    'Import/Export',
    'Other',
  ];

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 pt-24 flex items-center justify-center" data-testid="join-speaker-page">
        <Card className="max-w-md mx-4 border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-navy-700 mb-3">Application Received!</h2>
            <p className="text-slate-600 mb-6">
              Thank you for your interest in joining our speaker panel. Our team will review 
              your application and contact you within 5 business days.
            </p>
            <Button 
              onClick={() => window.location.href = '/'}
              className="bg-navy-700 hover:bg-navy-800 text-white rounded-xl"
            >
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="join-speaker-page">
      {/* Header */}
      <section className="hero-canvas border-b border-zinc-200 pt-28 pb-16 relative overflow-hidden">
        <div className="container-custom relative z-10">
          <Breadcrumb />
          <div className="w-full">
            <span className="mono-label inline-flex items-center gap-2 text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded-full px-3 py-1 mb-5">
              <Mic className="w-3 h-3" />
              Join Our Team
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-[-0.03em] text-zinc-950 mb-4">
              Become a Speaker
            </h1>
            <p className="text-zinc-500 text-lg">
              Share your expertise with thousands of GRC professionals. Join our panel of 
              industry-leading speakers and make an impact.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="text-center group">
                <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-navy-100 flex items-center justify-center group-hover:bg-navy-200 transition-colors">
                  <benefit.icon className="w-6 h-6 text-navy-600" />
                </div>
                <h3 className="font-semibold text-navy-700 mb-1">{benefit.title}</h3>
                <p className="text-sm text-slate-500">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-navy-700 mb-6">
                  Speaker Application
                </h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="firstName" className="text-sm">First Name *</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="John"
                        required
                        className="h-10 rounded-lg"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="lastName" className="text-sm">Last Name *</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Doe"
                        required
                        className="h-10 rounded-lg"
                      />
                    </div>
                  </div>

                  {/* Contact Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-sm">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        required
                        className="h-10 rounded-lg"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="phone" className="text-sm">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (555) 000-0000"
                        required
                        className="h-10 rounded-lg"
                      />
                    </div>
                  </div>

                  {/* Professional Info Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="company" className="text-sm">Company/Organization *</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your company"
                        required
                        className="h-10 rounded-lg"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="title" className="text-sm">Job Title *</Label>
                      <Input
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        placeholder="Chief Compliance Officer"
                        required
                        className="h-10 rounded-lg"
                      />
                    </div>
                  </div>

                  {/* LinkedIn */}
                  <div className="space-y-1.5">
                    <Label htmlFor="linkedin" className="text-sm">LinkedIn Profile URL</Label>
                    <Input
                      id="linkedin"
                      name="linkedin"
                      value={formData.linkedin}
                      onChange={handleChange}
                      placeholder="https://linkedin.com/in/yourprofile"
                      className="h-10 rounded-lg"
                    />
                  </div>

                  {/* Expertise & Experience Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="expertise" className="text-sm">Primary Expertise *</Label>
                      <Select 
                        value={formData.expertise} 
                        onValueChange={(v) => handleSelectChange('expertise', v)}
                      >
                        <SelectTrigger className="h-10 rounded-lg">
                          <SelectValue placeholder="Select area" />
                        </SelectTrigger>
                        <SelectContent>
                          {expertiseAreas.map((area) => (
                            <SelectItem key={area} value={area}>{area}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="experience" className="text-sm">Years of Experience *</Label>
                      <Select 
                        value={formData.experience} 
                        onValueChange={(v) => handleSelectChange('experience', v)}
                      >
                        <SelectTrigger className="h-10 rounded-lg">
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5-10">5-10 years</SelectItem>
                          <SelectItem value="10-15">10-15 years</SelectItem>
                          <SelectItem value="15-20">15-20 years</SelectItem>
                          <SelectItem value="20+">20+ years</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Topics */}
                  <div className="space-y-1.5">
                    <Label htmlFor="topics" className="text-sm">Proposed Webinar Topics *</Label>
                    <Textarea
                      id="topics"
                      name="topics"
                      value={formData.topics}
                      onChange={handleChange}
                      placeholder="List 2-3 topics you'd like to present on..."
                      required
                      rows={3}
                      className="rounded-lg resize-none"
                    />
                  </div>

                  {/* Bio */}
                  <div className="space-y-1.5">
                    <Label htmlFor="bio" className="text-sm">Professional Bio *</Label>
                    <Textarea
                      id="bio"
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      placeholder="Brief overview of your professional background and qualifications..."
                      required
                      rows={4}
                      className="rounded-lg resize-none"
                    />
                  </div>

                  {/* Terms Checkbox */}
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                    <Checkbox
                      id="agreeTerms"
                      name="agreeTerms"
                      checked={formData.agreeTerms}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, agreeTerms: checked }))}
                      className="mt-0.5"
                    />
                    <Label htmlFor="agreeTerms" className="text-sm text-slate-600 cursor-pointer">
                      I agree to the <a href="/terms-of-use" className="text-navy-600 hover:underline">Terms of Use</a> and 
                      acknowledge that my application will be reviewed by the GRC Educators team.
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11 bg-gold-500 hover:bg-gold-600 text-navy-800 font-semibold rounded-xl transition-all duration-300"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Submit Application
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
